<script setup>
import { useAbility } from '@casl/vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import RiskAssessmentService from '@/services/quality/risk-assessment.service'
import { riskAssessmentStatusColor, riskAssessmentStatusTitle } from '@/enums/quality/risk-assessment-status'
import { assessmentRiskRoleColor, assessmentRiskRoleTitle } from '@/enums/quality/assessment-risk-role'
import { riskLevelColor, riskLevelTitle } from '@/enums/quality/risk-level'
import AssessmentRiskLinkDialog from '@/views/quality/risk/assessment/AssessmentRiskLinkDialog.vue'
import AssessmentCompleteDialog from '@/views/quality/risk/assessment/AssessmentCompleteDialog.vue'
import AssessmentQuestionnaire from '@/views/quality/risk/assessment/AssessmentQuestionnaire.vue'

definePage({
  meta: {
    subject: 'risk.menu',
    action: 'assessments',
  },
})

const route = useRoute()
const router = useRouter()
const { can } = useAbility()

const assessment = ref(null)
const isLoading = ref(false)
const isLinkDialogVisible = ref(false)
const isCompleteDialogVisible = ref(false)

let fetchToken = 0

const fetchAssessment = async () => {
  const token = ++fetchToken

  isLoading.value = true

  // useApi's own onError never fires; the service callback is the only error
  // signal. Leaving assessment null while isLoading goes false is what the
  // template's final v-else renders — without it the page would be blank.
  await RiskAssessmentService.show(route.params.id, data => {
    if (token !== fetchToken) return
    assessment.value = data?.data ?? data ?? null
    isLoading.value = false
  }, () => {
    if (token !== fetchToken) return
    assessment.value = null
    isLoading.value = false
  })
}

fetchAssessment()

const isTerminal = computed(() => ['COMPLETED', 'CANCELLED'].includes(assessment.value?.status))
const linkedRisks = computed(() => assessment.value?.risks ?? [])
const linkedRiskIds = computed(() => linkedRisks.value.map(r => r.id))

// Only live risks satisfy the completion invariant server-side, so the dialog
// must be told the live count, not the displayed one.
const liveLinkedCount = computed(() => linkedRisks.value.filter(r => !r.deleted).length)
const canEdit = computed(() => can('assess', 'quality.risk') && !isTerminal.value)

// Questionnaire state, reported up by AssessmentQuestionnaire's `loaded` event so
// the Complete dialog can block on it without a second request.
//
// requiredOutstanding is an ARRAY of question ids, not a count — the server is
// the single authority for the rule. Never compare it to 0 directly: in JS
// `[3] > 0` is true but `[3,4] > 0` is false, so a count comparison would fire
// erratically by outstanding-count. Always pass `.length`.
const questionnaire = ref({ answered: 0, total: 0, requiredOutstanding: [], frozen: false })
const questionnaireRef = ref(null)

const onQuestionnaireLoaded = state => {
  questionnaire.value = state
}

// PER_EVENT types (PCRA) are the only ones that carry project context.
const isPerEvent = computed(() => assessment.value?.risk_type?.cadence_mode === 'PER_EVENT')

const hasProjectContext = computed(() => Boolean(
  assessment.value?.project_location
  || assessment.value?.project_start
  || assessment.value?.project_end
  || assessment.value?.project_reference,
))

const onLink = async payload => {
  await RiskAssessmentService.linkRisk(route.params.id, payload, () => {
    isLinkDialogVisible.value = false
    fetchAssessment()
  }, () => {
    isLinkDialogVisible.value = false
  })
}

const onUnlink = async riskId => {
  await RiskAssessmentService.unlinkRisk(route.params.id, riskId, fetchAssessment)
}

const onComplete = async payload => {
  await RiskAssessmentService.complete(route.params.id, payload, () => {
    isCompleteDialogVisible.value = false
    fetchAssessment()

    // The questionnaire must be refetched too: completion freezes it, so the
    // live inputs have to be replaced by the snapshot's read-only rendering.
    questionnaireRef.value?.reload()
  }, () => {
    isCompleteDialogVisible.value = false
    fetchAssessment()
  })
}

const onCancel = async () => {
  await RiskAssessmentService.cancel(route.params.id, fetchAssessment)
}
</script>

<template>
  <section v-if="assessment">
    <div class="d-flex align-center gap-4 mb-6">
      <!--
        Matches the risk detail page's back control (IconBtn, tonal, rounded,
        size large). This was a secondary-coloured VBtn, which renders as a
        plain grey circle and read as disabled next to the identical-purpose
        control one screen away.
      -->
      <IconBtn
        variant="tonal"
        class="rounded"
        size="large"
        @click="router.push({ name: 'app-quality-risk-assessments' })"
      >
        <VIcon
          size="24"
          icon="tabler-arrow-left"
        />
      </IconBtn>
      <div>
        <div class="d-flex align-center gap-3">
          <span class="text-h5">{{ assessment.title }}</span>
          <VChip
            :color="riskAssessmentStatusColor(assessment.status)"
            size="small"
            label
          >
            {{ riskAssessmentStatusTitle(assessment.status) }}
          </VChip>
        </div>
        <span class="text-body-2 text-medium-emphasis">
          {{ assessment.risk_type?.name }}
        </span>
      </div>
    </div>

    <div
      v-if="canEdit"
      class="d-flex flex-wrap gap-3 mb-6"
    >
      <VBtn
        prepend-icon="tabler-link"
        variant="tonal"
        @click="isLinkDialogVisible = true"
      >
        Link Risk
      </VBtn>
      <VBtn
        prepend-icon="tabler-check"
        color="success"
        @click="isCompleteDialogVisible = true"
      >
        Complete
      </VBtn>
      <VBtn
        prepend-icon="tabler-x"
        variant="tonal"
        color="error"
        @click="onCancel"
      >
        Cancel
      </VBtn>
    </div>

    <VRow>
      <VCol
        cols="12"
        md="5"
      >
        <VCard
          variant="flat"
          class="border h-100"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">Assessment Details</span>
            </template>
          </VCardItem>
          <VCardText class="d-flex flex-column gap-2">
            <div class="d-flex align-center text-body-2">
              <span class="review-label text-medium-emphasis">Scope:</span>
              <span class="font-weight-medium">{{ assessment.scope_description ?? '—' }}</span>
            </div>
            <div class="d-flex align-center text-body-2">
              <span class="review-label text-medium-emphasis">Started:</span>
              <span class="font-weight-medium">{{ assessment.started_at ? formatDate(assessment.started_at) : '—' }}</span>
            </div>
            <div class="d-flex align-center text-body-2">
              <span class="review-label text-medium-emphasis">Performed by:</span>
              <span class="font-weight-medium">{{ assessment.performer?.name ?? '—' }}</span>
            </div>
            <div class="d-flex align-center text-body-2">
              <span class="review-label text-medium-emphasis">Completed:</span>
              <span class="font-weight-medium">{{ assessment.completed_at ? formatDate(assessment.completed_at) : '—' }}</span>
            </div>
            <!--
              Project context, for PER_EVENT types only. Rendered when the type
              is PER_EVENT or when values exist at all — so a type changed after
              the fact never hides data that was already recorded.
            -->
            <template v-if="isPerEvent || hasProjectContext">
              <VDivider class="my-2" />
              <div class="d-flex align-center text-body-2">
                <span class="review-label text-medium-emphasis">Project location:</span>
                <span class="font-weight-medium">{{ assessment.project_location ?? '—' }}</span>
              </div>
              <div class="d-flex align-center text-body-2">
                <span class="review-label text-medium-emphasis">Project start:</span>
                <span class="font-weight-medium">{{ assessment.project_start ? formatDate(assessment.project_start) : '—' }}</span>
              </div>
              <div class="d-flex align-center text-body-2">
                <span class="review-label text-medium-emphasis">Project end:</span>
                <span class="font-weight-medium">{{ assessment.project_end ? formatDate(assessment.project_end) : '—' }}</span>
              </div>
              <div class="d-flex align-center text-body-2">
                <span class="review-label text-medium-emphasis">Reference:</span>
                <span class="font-weight-medium">{{ assessment.project_reference ?? '—' }}</span>
              </div>
            </template>

            <VAlert
              v-if="assessment.nil_return"
              type="info"
              variant="tonal"
              class="mt-3"
            >
              <span class="font-weight-medium">Nil return.</span>
              {{ assessment.nil_return_rationale }}
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="7"
      >
        <VCard
          variant="flat"
          class="border h-100"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">Risks ({{ linkedRisks.length }})</span>
            </template>
          </VCardItem>
          <VCardText>
            <div
              v-if="linkedRisks.length"
              class="d-flex flex-column gap-3"
            >
              <div
                v-for="risk in linkedRisks"
                :key="risk.id"
                class="d-flex align-center justify-space-between gap-3"
              >
                <div class="d-flex align-center gap-3">
                  <VChip
                    :color="assessmentRiskRoleColor(risk.role)"
                    size="small"
                    label
                  >
                    {{ assessmentRiskRoleTitle(risk.role) }}
                  </VChip>
                  <RouterLink :to="{ name: 'app-quality-risk-id', params: { id: risk.id } }">
                    {{ risk.code }} — {{ risk.title }}
                  </RouterLink>
                  <VChip
                    v-if="risk.deleted"
                    color="grey-500"
                    size="small"
                    label
                  >
                    Deleted
                  </VChip>
                </div>
                <div class="d-flex align-center gap-2">
                  <VChip
                    v-if="risk.inherent_risk_level"
                    :color="riskLevelColor(risk.inherent_risk_level)"
                    size="small"
                    label
                  >
                    {{ riskLevelTitle(risk.inherent_risk_level) }}
                  </VChip>
                  <VBtn
                    v-if="canEdit"
                    icon="tabler-unlink"
                    variant="text"
                    size="small"
                    color="error"
                    @click="onUnlink(risk.id)"
                  />
                </div>
              </div>
            </div>
            <p
              v-else
              class="text-body-2 text-medium-emphasis mb-0"
            >
              No risks linked yet. Link the risks this assessment identified or re-examined, or complete it as a nil return.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!--
      Below the Risks row. The component renders nothing at all when the type has
      no checklist, so no v-if on total is needed here.
    -->
    <AssessmentQuestionnaire
      ref="questionnaireRef"
      :assessment-id="assessment.id"
      :can-answer="canEdit"
      class="mt-6"
      @loaded="onQuestionnaireLoaded"
    />

    <AssessmentRiskLinkDialog
      v-model:is-dialog-visible="isLinkDialogVisible"
      :unit-id="assessment.unit_id"
      :exclude-risk-ids="linkedRiskIds"
      @link="onLink"
    />

    <AssessmentCompleteDialog
      v-model:is-dialog-visible="isCompleteDialogVisible"
      :linked-count="liveLinkedCount"
      :unit-id="assessment.unit_id"
      :answered-count="questionnaire.answered"
      :total-count="questionnaire.total"
      :required-outstanding="questionnaire.requiredOutstanding.length"
      @complete="onComplete"
    />
  </section>

  <section v-else-if="isLoading">
    <VSkeletonLoader type="card" />
  </section>

  <section v-else>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="text-center py-12">
        <VIcon
          icon="tabler-alert-triangle"
          size="48"
          color="warning"
          class="mb-4"
        />
        <h6 class="text-h6 mb-2">
          This assessment could not be loaded
        </h6>
        <p class="text-body-2 text-medium-emphasis mb-6">
          It may have been deleted, or it belongs to a unit you do not have access to.
        </p>
        <VBtn
          color="primary"
          :to="{ name: 'app-quality-risk-assessments' }"
        >
          Back to assessments
        </VBtn>
      </VCardText>
    </VCard>
  </section>
</template>

<style lang="scss" scoped>
.review-label {
  flex-shrink: 0;
  inline-size: 120px;
}
</style>
